import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api.js';
import toast from 'react-hot-toast';
import { AlertCircle, RotateCcw, Eye, FileText, X } from 'lucide-react';
import Swal from 'sweetalert2';

export default function DevolucionTramiteModal({ tramite, onClose, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [cotizacionesPagadas, setCotizacionesPagadas] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [montoDevolucion, setMontoDevolucion] = useState('');
  const [motivoDevolucion, setMotivoDevolucion] = useState('');
  const [finalizarTramite, setFinalizarTramite] = useState(false);
  const [detalleModal, setDetalleModal] = useState(null);
  const [aplicarRetencion, setAplicarRetencion] = useState(false);
  const [tasaRetencion, setTasaRetencion] = useState(15.25);
  const [montoBrutoBase, setMontoBrutoBase] = useState(0);

  useEffect(() => {
    // Obtener tasa de retención legal configurada
    api.get('/parametros/retencion')
      .then(res => {
        if (res.data?.tasa_retencion) {
          setTasaRetencion(Number(res.data.tasa_retencion));
        }
      })
      .catch(() => {
        setTasaRetencion(15.25);
      });
  }, []);

  useEffect(() => {
    const fetchCotizaciones = async () => {
      setFetching(true);
      try {
        const res = await api.get(`/tramites/${tramite.id}/cotizacion/historial`);
        const list = (res.data || []).filter(c => c.estado === 'pagada');
        setCotizacionesPagadas(list);

        if (list.length > 0) {
          const allIds = list.map(c => c.id);
          setSelectedIds(allIds);
          const sumTotal = list.reduce((acc, c) => acc + (parseFloat(c.total) || 0), 0);
          const rounded = Math.round(sumTotal);
          setMontoBrutoBase(rounded);
          setMontoDevolucion(rounded);
        } else if (tramite.cotizacion) {
          const fallbackTotal = parseFloat(tramite.cotizacion.total) || 0;
          const rounded = Math.round(fallbackTotal);
          setMontoBrutoBase(rounded);
          setMontoDevolucion(rounded);
        }
      } catch (err) {
        console.error('Error al cargar cotizaciones pagadas:', err);
        const fallbackTotal = parseFloat(tramite.cotizacion?.total) || 0;
        const rounded = Math.round(fallbackTotal);
        setMontoBrutoBase(rounded);
        setMontoDevolucion(rounded);
      } finally {
        setFetching(false);
      }
    };

    fetchCotizaciones();
  }, [tramite.id, tramite.cotizacion]);

  const totalSeleccionado = useMemo(() => {
    return cotizacionesPagadas
      .filter(c => selectedIds.includes(c.id))
      .reduce((acc, c) => acc + (parseFloat(c.total) || 0), 0);
  }, [cotizacionesPagadas, selectedIds]);

  const totalGeneralPagado = useMemo(() => {
    if (cotizacionesPagadas.length > 0) {
      return cotizacionesPagadas.reduce((acc, c) => acc + (parseFloat(c.total) || 0), 0);
    }
    return parseFloat(tramite.cotizacion?.total) || 0;
  }, [cotizacionesPagadas, tramite.cotizacion]);

  const handleToggleSelectAll = () => {
    if (selectedIds.length === cotizacionesPagadas.length) {
      setSelectedIds([]);
      setMontoBrutoBase(0);
      setMontoDevolucion(0);
    } else {
      const allIds = cotizacionesPagadas.map(c => c.id);
      setSelectedIds(allIds);
      const sum = cotizacionesPagadas.reduce((acc, c) => acc + (parseFloat(c.total) || 0), 0);
      const rounded = Math.round(sum);
      setMontoBrutoBase(rounded);
      if (aplicarRetencion) {
        const ret = Math.round(rounded * (tasaRetencion / 100));
        setMontoDevolucion(Math.max(0, rounded - ret));
      } else {
        setMontoDevolucion(rounded);
      }
    }
  };

  const handleToggleId = (id) => {
    let nextIds;
    if (selectedIds.includes(id)) {
      nextIds = selectedIds.filter(item => item !== id);
    } else {
      nextIds = [...selectedIds, id];
    }
    setSelectedIds(nextIds);
    const sum = cotizacionesPagadas
      .filter(c => nextIds.includes(c.id))
      .reduce((acc, c) => acc + (parseFloat(c.total) || 0), 0);
    const rounded = Math.round(sum);
    setMontoBrutoBase(rounded);
    if (aplicarRetencion) {
      const ret = Math.round(rounded * (tasaRetencion / 100));
      setMontoDevolucion(Math.max(0, rounded - ret));
    } else {
      setMontoDevolucion(rounded);
    }
  };

  const handleToggleRetencion = (checked) => {
    setAplicarRetencion(checked);
    const base = montoBrutoBase > 0 ? montoBrutoBase : (Number(montoDevolucion) || 0);
    setMontoBrutoBase(base);
    if (checked) {
      const retencion = Math.round(base * (tasaRetencion / 100));
      setMontoDevolucion(Math.max(0, base - retencion));
      if (!motivoDevolucion.includes('retención impositiva')) {
        const nota = ` (Aplica descuento por retención impositiva del ${tasaRetencion}% correspondiente al mes ya declarado al SII).`;
        setMotivoDevolucion(prev => prev ? `${prev.trim()}${nota}` : `Devolución de fondos.${nota}`);
      }
    } else {
      setMontoDevolucion(base);
    }
  };

  const montoRetencionCalculado = useMemo(() => {
    if (!aplicarRetencion) return 0;
    const base = montoBrutoBase > 0 ? montoBrutoBase : (Number(montoDevolucion) || 0);
    return Math.round(base * (tasaRetencion / 100));
  }, [aplicarRetencion, montoBrutoBase, montoDevolucion, tasaRetencion]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const numericMonto = Number(montoDevolucion);

    if (montoDevolucion === '' || isNaN(numericMonto) || numericMonto < 0) {
      toast.error('Ingrese un monto de devolución válido.');
      return;
    }

    if (!motivoDevolucion || motivoDevolucion.trim() === '') {
      toast.error('Ingrese el motivo de la devolución.');
      return;
    }

    const result = await Swal.fire({
      title: '¿Registrar Devolución?',
      text: finalizarTramite
        ? `Se devolverá un monto de $${new Intl.NumberFormat('es-CL').format(numericMonto)} y el trámite se dará por FINALIZADO / CERRADO definitivamente. ¿Desea continuar?`
        : `Se devolverá un monto de $${new Intl.NumberFormat('es-CL').format(numericMonto)} y el trámite quedará en estado "Devuelto" (podrá entregar y finalizar los documentos restantes más adelante).`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#64748b',
      confirmButtonText: finalizarTramite ? 'Sí, devolver y finalizar trámite' : 'Sí, registrar devolución',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      await api.post(`/tramites/${tramite.id}/devolucion`, {
        monto_devolucion: numericMonto,
        motivo_devolucion: motivoDevolucion,
        cotizaciones_ids: selectedIds,
        finalizar_tramite: finalizarTramite,
        aplicar_retencion: aplicarRetencion,
        tasa_retencion: tasaRetencion,
        monto_bruto: aplicarRetencion ? montoBrutoBase : numericMonto,
        monto_retencion: montoRetencionCalculado
      });
      toast.success(finalizarTramite ? 'Devolución registrada y trámite finalizado.' : 'Devolución registrada exitosamente.');
      onUpdate();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || 'Error al registrar la devolución.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" style={{ maxWidth: '560px', width: '95%', padding: 0, borderRadius: '12px', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
          {/* Encabezado */}
          <div className="modal-header" style={{ padding: '1.2rem 1.5rem', borderBottom: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ padding: '8px', background: '#fee2e2', color: '#dc2626', borderRadius: '8px', display: 'flex' }}>
                <RotateCcw size={18} />
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-color, #0f172a)' }}>Registrar Devolución</h3>
                <span style={{ fontSize: '0.78rem', color: 'var(--gray-500)', fontFamily: 'monospace' }}>Trámite #{tramite.numero_seguimiento}</span>
              </div>
            </div>
            <button className="modal-close" onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.2rem', cursor: 'pointer', color: 'var(--gray-500)' }}>✕</button>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ padding: '1.5rem' }}>
              {/* Banner Resumen */}
              <div style={{ marginBottom: '1.2rem', background: '#fef2f2', border: '1px solid #fecaca', padding: '12px 16px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <AlertCircle size={20} style={{ color: '#dc2626', flexShrink: 0 }} />
                  <span style={{ fontSize: '0.85rem', color: '#991b1b', fontWeight: 500 }}>Total pagado acumulado:</span>
                </div>
                <span style={{ fontSize: '1.15rem', fontWeight: 800, color: '#dc2626' }}>
                  ${new Intl.NumberFormat('es-CL').format(totalGeneralPagado)}
                </span>
              </div>

              {/* Tabla estilizada de cotizaciones pagadas */}
              <div style={{ marginBottom: '1.3rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--gray-600)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Pagos Realizados ({cotizacionesPagadas.length})
                  </span>
                  {cotizacionesPagadas.length > 1 && (
                    <button 
                      type="button" 
                      onClick={handleToggleSelectAll}
                      style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', padding: 0 }}
                    >
                      {selectedIds.length === cotizacionesPagadas.length ? 'Deseleccionar todas' : 'Seleccionar todas'}
                    </button>
                  )}
                </div>

                {fetching ? (
                  <div style={{ padding: '16px', textAlign: 'center', color: 'var(--gray-400)', fontSize: '0.85rem' }}>
                    Cargando pagos...
                  </div>
                ) : cotizacionesPagadas.length === 0 ? (
                  <div style={{ padding: '10px 12px', background: 'var(--bg-body, #f8fafc)', borderRadius: 6, fontSize: '0.85rem', color: 'var(--gray-500)' }}>
                    No se registran pagos previos.
                  </div>
                ) : (
                  <div style={{ border: '1px solid var(--border-color, #e2e8f0)', borderRadius: '8px', overflow: 'hidden', background: '#ffffff' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ background: 'var(--bg-body, #f8fafc)', borderBottom: '1px solid var(--border-color, #e2e8f0)', color: 'var(--gray-500)', fontSize: '0.72rem', textTransform: 'uppercase' }}>
                          <th style={{ width: '38px', padding: '8px', textAlign: 'center' }}>
                            <input 
                              type="checkbox"
                              checked={selectedIds.length === cotizacionesPagadas.length && cotizacionesPagadas.length > 0}
                              onChange={handleToggleSelectAll}
                              style={{ cursor: 'pointer', verticalAlign: 'middle' }}
                            />
                          </th>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Cotización</th>
                          <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600 }}>Fecha</th>
                          <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>Monto</th>
                          <th style={{ width: '100px', padding: '8px 10px', textAlign: 'center', fontWeight: 600 }}>Detalle</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cotizacionesPagadas.map((cot, idx) => {
                          const isSelected = selectedIds.includes(cot.id);
                          return (
                            <tr 
                              key={cot.id}
                              onClick={() => handleToggleId(cot.id)}
                              style={{
                                cursor: 'pointer',
                                borderBottom: idx < cotizacionesPagadas.length - 1 ? '1px solid #f1f5f9' : 'none',
                                backgroundColor: isSelected ? '#f0fdf4' : 'transparent',
                                transition: 'background-color 0.12s ease'
                              }}
                            >
                              <td style={{ padding: '9px 8px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                <input 
                                  type="checkbox" 
                                  checked={isSelected}
                                  onChange={() => handleToggleId(cot.id)}
                                  style={{ cursor: 'pointer', verticalAlign: 'middle' }}
                                />
                              </td>
                              <td style={{ padding: '9px 10px', fontWeight: 600, color: 'var(--text-color, #1e293b)', whiteSpace: 'nowrap' }}>
                                #{cot.id}
                                {idx === 0 && (
                                  <span style={{ fontSize: '0.65rem', background: '#dbeafe', color: '#1d4ed8', padding: '2px 5px', borderRadius: 4, marginLeft: 5, fontWeight: 700 }}>
                                    Última
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '9px 10px', color: 'var(--gray-500)', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                                {new Date(cot.created_at).toLocaleDateString('es-CL')} {new Date(cot.created_at).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' })}
                              </td>
                              <td style={{ padding: '9px 10px', textAlign: 'right', fontWeight: 700, color: '#059669', whiteSpace: 'nowrap' }}>
                                ${new Intl.NumberFormat('es-CL').format(cot.total)}
                              </td>
                              <td style={{ padding: '9px 10px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                <button
                                  type="button"
                                  onClick={() => setDetalleModal(cot)}
                                  style={{
                                    background: '#ffffff',
                                    border: '1px solid #cbd5e1',
                                    color: '#334155',
                                    fontSize: '0.72rem',
                                    fontWeight: 600,
                                    padding: '3px 8px',
                                    borderRadius: '5px',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    cursor: 'pointer',
                                    transition: 'all 0.15s ease'
                                  }}
                                  onMouseOver={e => e.currentTarget.style.borderColor = '#0284c7'}
                                  onMouseOut={e => e.currentTarget.style.borderColor = '#cbd5e1'}
                                  title="Ver desglose de ítems cotizados"
                                >
                                  <Eye size={12} color="#0284c7" /> Ver Detalle
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Input de Monto a Devolver con formateo de puntos de miles */}
              <div className="form-group" style={{ marginBottom: '1.2rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <label style={{ margin: 0, fontWeight: 600, fontSize: '0.85rem', color: 'var(--gray-700)' }}>
                    Monto {aplicarRetencion ? 'Líquido a Devolver' : 'a Devolver'} ($)
                  </label>
                  {totalSeleccionado > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        const rounded = Math.round(totalSeleccionado);
                        setMontoBrutoBase(rounded);
                        if (aplicarRetencion) {
                          const ret = Math.round(rounded * (tasaRetencion / 100));
                          setMontoDevolucion(Math.max(0, rounded - ret));
                        } else {
                          setMontoDevolucion(rounded);
                        }
                      }}
                      style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', padding: '2px 8px', borderRadius: 4 }}
                    >
                      Copiar total seleccionado (${new Intl.NumberFormat('es-CL').format(totalSeleccionado)})
                    </button>
                  )}
                </div>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontWeight: 700, fontSize: '1.2rem', color: 'var(--gray-400)' }}>$</span>
                  <input 
                    type="text" 
                    className="form-control"
                    value={montoDevolucion !== '' && montoDevolucion !== null && !isNaN(montoDevolucion) ? new Intl.NumberFormat('es-CL').format(montoDevolucion) : ''}
                    onChange={(e) => {
                      const clean = e.target.value.replace(/\D/g, '');
                      const val = clean === '' ? '' : parseInt(clean, 10);
                      setMontoDevolucion(val);
                      if (!aplicarRetencion) {
                        setMontoBrutoBase(val || 0);
                      }
                    }}
                    placeholder="0"
                    required
                    style={{ fontSize: '1.25rem', fontWeight: 700, padding: '10px 14px 10px 30px', width: '100%', borderRadius: '8px', border: '1px solid var(--border-color, #cbd5e1)' }}
                  />
                </div>
              </div>

              {/* Casilla de Retención Impositiva Ley 21.133 */}
              <div style={{
                marginBottom: '1.2rem',
                padding: '12px 14px',
                background: aplicarRetencion ? 'rgba(234, 88, 12, 0.06)' : '#f8fafc',
                border: `1px solid ${aplicarRetencion ? '#f97316' : '#e2e8f0'}`,
                borderRadius: '8px',
                transition: 'all 0.2s'
              }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', margin: 0 }}>
                  <input 
                    type="checkbox"
                    checked={aplicarRetencion}
                    onChange={(e) => handleToggleRetencion(e.target.checked)}
                    style={{ width: '16px', height: '16px', marginTop: '2px', cursor: 'pointer', accentColor: '#ea580c' }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '6px' }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 700, color: aplicarRetencion ? '#c2410c' : '#1e293b' }}>
                        Descontar Retención Impositiva ({tasaRetencion}% - Ley 21.133)
                      </span>
                      <span style={{ fontSize: '0.72rem', background: '#ffedd5', color: '#9a3412', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>
                        Impuesto del mes declarado al SII
                      </span>
                    </div>
                    <p style={{ margin: '4px 0 0', fontSize: '0.75rem', color: '#64748b', lineHeight: 1.4 }}>
                      Marque esta opción si el impuesto del mes correspondiente ya fue declarado ante el SII. Se descontará automáticamente el {tasaRetencion}% legal.
                    </p>

                    {aplicarRetencion && (
                      <div style={{ marginTop: '10px', padding: '10px 12px', background: '#fff', borderRadius: '6px', border: '1px solid #fed7aa', fontSize: '0.82rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ color: '#64748b' }}>Monto Bruto Seleccionado:</span>
                          <span style={{ fontWeight: 600 }}>${new Intl.NumberFormat('es-CL').format(montoBrutoBase)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', color: '#dc2626' }}>
                          <span>Retención Legal ({tasaRetencion}%):</span>
                          <span style={{ fontWeight: 600 }}>-${new Intl.NumberFormat('es-CL').format(montoRetencionCalculado)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '6px', borderTop: '1px dashed #e2e8f0', color: '#059669', fontWeight: 800 }}>
                          <span>Total Líquido a Devolver:</span>
                          <span>${new Intl.NumberFormat('es-CL').format(montoDevolucion)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </label>
              </div>

              {/* Motivo */}
              <div className="form-group">
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 600, fontSize: '0.85rem', color: 'var(--gray-700)' }}>
                  Motivo de la Devolución
                </label>
                <textarea 
                  className="form-control"
                  value={motivoDevolucion}
                  onChange={(e) => setMotivoDevolucion(e.target.value)}
                  placeholder="Indique la justificación formal de la devolución (ej. Desistimiento voluntario del usuario, error en monto cobrado...)"
                  rows={3}
                  required
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border-color, #cbd5e1)', fontSize: '0.88rem' }}
                />
              </div>

              {/* Opción de dar por terminado/cerrado el trámite */}
              <div style={{ marginTop: '1.2rem', padding: '12px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', margin: 0 }}>
                  <input 
                    type="checkbox"
                    checked={finalizarTramite}
                    onChange={(e) => setFinalizarTramite(e.target.checked)}
                    style={{ width: '16px', height: '16px', marginTop: '2px', cursor: 'pointer' }}
                  />
                  <div>
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b' }}>
                      Dar por terminado / cerrado el trámite con esta devolución
                    </span>
                    <p style={{ margin: '3px 0 0', fontSize: '0.75rem', color: '#64748b', lineHeight: 1.4 }}>
                      Márquelo si el trámite concluye totalmente aquí (por ejemplo, si no se realizarán inscripciones ni se entregarán documentos). Si aún debe entregar otros documentos, déjelo desmarcado para finalizar la entrega después con el botón "Finalizar".
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer" style={{ padding: '1rem 1.5rem', background: '#f8fafc', borderTop: '1px solid var(--border-color)', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button type="button" className="btn btn-outline" onClick={onClose} disabled={loading} style={{ padding: '0.55rem 1.2rem', borderRadius: '8px' }}>
                Cancelar
              </button>
              <button type="submit" className="btn btn-danger" disabled={loading} style={{ background: '#dc2626', color: 'white', border: 'none', padding: '0.55rem 1.4rem', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}>
                {loading ? <span className="spinner" /> : 'Confirmar Devolución'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal Secundario: Detalle de Ítems de la Cotización */}
      {detalleModal && (
        <div className="modal-overlay" style={{ zIndex: 1200 }} onClick={() => setDetalleModal(null)}>
          <div className="modal-content" style={{ maxWidth: '480px', width: '92%', padding: 0, borderRadius: '12px', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header" style={{ padding: '1rem 1.3rem', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FileText size={18} color="var(--primary)" />
                <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700 }}>Detalle Cotización #{detalleModal.id}</h4>
              </div>
              <button onClick={() => setDetalleModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: 'var(--gray-500)' }}>✕</button>
            </div>
            <div style={{ padding: '1.2rem', maxHeight: '380px', overflowY: 'auto' }}>
              <div style={{ fontSize: '0.8rem', color: 'var(--gray-500)', marginBottom: '12px' }}>
                Fecha emisión: {new Date(detalleModal.created_at).toLocaleString('es-CL', { hour12: false, dateStyle: 'long', timeStyle: 'short' })}
              </div>

              <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.83rem' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b', fontSize: '0.72rem', textTransform: 'uppercase' }}>
                      <th style={{ padding: '8px 10px', textAlign: 'left' }}>Ítem</th>
                      <th style={{ width: '60px', padding: '8px', textAlign: 'center' }}>Cant.</th>
                      <th style={{ padding: '8px 10px', textAlign: 'right' }}>Monto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(detalleModal.items || []).map((it, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '8px 10px', fontWeight: 500, color: '#1e293b' }}>
                          {it.descripcion}
                        </td>
                        <td style={{ padding: '8px', textAlign: 'center', color: '#64748b' }}>
                          {it.cantidad}
                        </td>
                        <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: it.monto < 0 ? '#059669' : '#1e293b', whiteSpace: 'nowrap' }}>
                          {it.monto < 0 ? `-$${new Intl.NumberFormat('es-CL').format(Math.abs(it.monto))}` : `$${new Intl.NumberFormat('es-CL').format(it.monto)}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {detalleModal.descuento_monto > 0 && (
                <div style={{ marginTop: '10px', padding: '8px 12px', background: '#ecfdf5', borderRadius: '6px', fontSize: '0.8rem', color: '#047857', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Descuento Global aplicado:</span>
                  <strong>{detalleModal.descuento_tipo === 'porcentaje' ? `${detalleModal.descuento_monto}%` : `-$${new Intl.NumberFormat('es-CL').format(detalleModal.descuento_monto)}`}</strong>
                </div>
              )}

              <div style={{ marginTop: '14px', paddingTop: '10px', borderTop: '2px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b' }}>Total Pagado:</span>
                <span style={{ fontWeight: 800, fontSize: '1.15rem', color: '#059669' }}>
                  ${new Intl.NumberFormat('es-CL').format(detalleModal.total)}
                </span>
              </div>
            </div>
            <div style={{ padding: '0.8rem 1.2rem', background: '#f8fafc', borderTop: '1px solid #e2e8f0', textAlign: 'right' }}>
              <button 
                type="button" 
                onClick={() => setDetalleModal(null)} 
                className="btn btn-outline"
                style={{ padding: '4px 14px', fontSize: '0.82rem', borderRadius: '6px' }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
